import React, { useEffect, useMemo, useState } from "react";
import Breadcrumbs from "../../common/Breadcrumbs";
import { useRole } from "../../context/RoleContext";
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  IconButton,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TextField,
  Typography,
} from "@mui/material";
import {
  mainContentStyle,
  paginationCaptionStyle,
  paginationContainerStyle,
} from "../customerManagement/DisplayCustomers.styles";
import DataTable from "../../common/DataTable";
import {
  addNewButtonStyle,
  cellStyle,
  grandTotalAmountStyle,
  grandTotalStyle,
  labelStyle,
  nameStyle,
  grandTotalMobVer,
  paginationMobVer,
} from "./ledgerStyles";
import { json2csv } from "json-2-csv";
import AddIcon from "@mui/icons-material/Add";
import downloadIcon from "../../assets/downloadNoBG.svg";
import SortBy from "../../common/filters/SortBy";
import DateRange from "../../common/filters/DateRange";
import { useAppSelector } from "../../redux/hooks";
import SearchBar from "../../common/filters/SearchBar";
import moment from "moment";
import { downloadLedger, getLedgerList } from "../../api/ledgerAPI";
import { LedgerResponseType, SelectedEntryType } from "../../constants/types";
import { useLocation } from "react-router-dom";
import { getCustomerById } from "../../api/customerAPI";
import { AddNewLedger } from "./AddNewLedger";
import useIsMobile from "../../common/mobileVersion/useIsMobile";

const Ledger = () => {
  const { role } = useRole();
  const location = useLocation();
  const isMobile = useIsMobile(); // Use the custom hook
  const customer = useAppSelector((state) => state.app.customerData?.customer);
  const isSeller = role === "seller" || location.pathname.startsWith("/seller");
  const [open, setOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<SelectedEntryType | null>(
    null
  );
  const [resturantName, setResturantName] = useState(
    isSeller ? undefined : customer?.outletName
  );
  const customerId = useMemo(() => {
    return isSeller ? location.pathname.split("/").at(-1) : customer?.id;
  }, [isSeller, location.pathname, customer?.id]);
  const breadcrumbPaths = [
    `${isSeller ? "All Customers" : "Profile"}`,
    "Ledger",
  ];
  const breadcrumbLinks = [
    `${isSeller ? "seller/all-customers" : "customer/customer-profile"}`,
  ];

  const columns = [
    { label: "Date", field: "date" },
    { label: "Order ID", field: "orderId" },
    { label: "Debit", field: "debit" },
    { label: "Credit", field: "credit" },
  ];

  const options = ["Newest", "Oldest"];
  const [sortBy, setSortBy] = useState(options[0]);
  const [from, setFrom] = useState<Date | undefined>(undefined);
  const [to, setTo] = useState<Date | undefined>(undefined);
  const [searchTerm, setSearchTerm] = useState("");
  const [ledgerData, setLedgerData] = useState<LedgerResponseType | undefined>(
    undefined
  );

  const [pagination, setPagination] = useState({
    page: 1,
    rowsPerPage: 10,
    totalRows: 0,
    totalPages: 1,
  });

  const handleEdit = (item: any) => {
    setSelectedEntry({
      id: item.id,
      orderId: item.orderId,
      creditAmount: item.credit,
      date: item.createdAt,
    });
    setOpen(true);
  };

  const tableData =
    (ledgerData?.data ?? []).map((item) => ({
      date: moment(item.createdAt).format("DD MMM YYYY"),
      orderId: item.debit ? item.id : item.orderId,
      debit: item.debit,
      credit: item.debit ? (
        <Box sx={{ height: "45px" }} />
      ) : isSeller ? (
        <TextField
          key={item.id}
          value={item.credit}
          variant="standard"
          sx={{
            input: {
              textAlign: "center",
              py: "12px",
              cursor: "pointer",
            },
          }}
          onClick={() => handleEdit(item)}
          InputProps={{ readOnly: true }}
        />
      ) : (
        item.credit
      ),
    })) ?? [];

  const handleDownload = async () => {
    const { rowsPerPage, page } = pagination;
    const queryParams = {
      customerId: customerId ?? "",
      page,
      rowsPerPage,
      sort: sortBy === "Newest" ? "desc" : "asc",
      startDate: (from && moment(from).format("YYYY-MM-DD")) ?? "",
      endDate: (from && moment(to).format("YYYY-MM-DD")) ?? "",
    };
    try {
      const response = await downloadLedger({
        ...queryParams,
        page: queryParams.page.toString(),
        rowsPerPage: queryParams.rowsPerPage.toString(),
      });

      if (!response || !response.data || response.data.length === 0) {
        console.error("No ledger data available to download.");
        return;
      }

      // Map ledger data to match the required columns
      const mappedData = response.data.map((entry) => ({
        Date: moment(entry.createdAt).format("DD MMM YYYY"), // Format date
        "Order ID": entry.debit ? entry.id : entry.orderId || "N/A", // Use `id` for debit or `orderId` for credit
        Debit: entry.debit || "0.00", // Default to "0.00" if no debit
        Credit: entry.credit || "0.00", // Default to "0.00" if no credit
      }));

      // Convert JSON to CSV using json2csv
      const csv = json2csv(mappedData);

      // Create a Blob and a temporary downloadable link
      const blob = new Blob([csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "ledger.csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error("Failed to download ledger data", err);
    }
  };

  const fetchLedgerData = async (page: string, limit: string) => {
    const queryParams = {
      customerId: customerId ?? "",
      page,
      limit: isMobile ? "1000" : "10",
      sort: sortBy === "Newest" ? "desc" : "asc",
      startDate: (from && moment(from).format("YYYY-MM-DD")) ?? "",
      endDate: (from && moment(to).format("YYYY-MM-DD")) ?? "",
      search: debouncedSearchTerm,
    };
    try {
      const response = await getLedgerList(queryParams);
      setLedgerData(response);
      if (!isMobile) {
        setPagination({
          page: Number(response?.pagination?.currentPage) ?? 1,
          rowsPerPage: 10,
          totalRows: Number(response?.pagination?.totalItems) ?? 1,
          totalPages: Number(response?.pagination?.totalPages) ?? 1,
        });
      }
    } catch (err) {
      console.error("Failed to fetch ledger data", err);
    }
  };

  const fetchCustomer = async () => {
    if (isSeller && customerId && !resturantName) {
      try {
        const response = await getCustomerById(customerId);
        setResturantName(response.data.data.customer.outletName);
      } catch (err) {
        console.error("Failed to fetch customer", err);
      }
    }
  };

  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 1500);
    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  useEffect(() => {
    if (debouncedSearchTerm || (debouncedSearchTerm === "" && customerId)) {
      fetchLedgerData("1", pagination.rowsPerPage.toString());
    }
  }, [debouncedSearchTerm, customerId]);

  useEffect(() => {
    fetchCustomer();
  }, [customerId, isSeller]);

  useEffect(() => {
    if (((from && to) || (!from && !to)) && customerId) {
      const rows = isMobile
        ? (ledgerData?.data?.length ?? 10).toString()
        : pagination.rowsPerPage.toString();
      fetchLedgerData("1", rows);
    }
  }, [sortBy, from, to, isMobile, customerId]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = e.target.value;
    setSearchTerm(searchValue);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleChangePage = (newPage: number) => {
    if (!isMobile) {
      setPagination((prev) => ({ ...prev, page: newPage }));
      fetchLedgerData(newPage.toString(), pagination.rowsPerPage.toString());
    }
  };

  const { page, rowsPerPage, totalPages, totalRows } = pagination;
  const startIndex = (+page - 1) * +rowsPerPage;
  const endIndex = Math.min(startIndex + +rowsPerPage, +totalRows);

  const grandTotalBlock = (
    <Table>
      <TableBody>
        {[
          { sx: grandTotalStyle, value: "GRAND TOTAL" },
          {},
          {},
          {
            sx: grandTotalAmountStyle(
              ledgerData
                ? +ledgerData.totalDebitAmount >= +ledgerData.totalCredit
                : true
            ),
            value: ledgerData
              ? Math.abs(+ledgerData.totalDebitAmount - +ledgerData.totalCredit)
              : 0,
          },
        ].map((item, idx) => (
          <TableCell
            key={`grandTotal_${idx}`}
            sx={{
              ...cellStyle("", isMobile),
              width: "26%",
              borderBottom: { xs: "none" },
            }}
          >
            <Typography sx={item.sx}>{item.value}</Typography>
          </TableCell>
        ))}
      </TableBody>
    </Table>
  );

  return (
    <>
      <Breadcrumbs paths={breadcrumbPaths} links={breadcrumbLinks} />
      {/* Search & Filters */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        flexWrap="wrap"
        mb="30px"
      >
        {!isMobile && (
          <SearchBar handleSearch={handleSearch} searchTerm={searchTerm} />
        )}

        <Box
          display="flex"
          justifyContent={{ xs: "space-between", sm: "flex-end" }}
          width={{ xs: "100%", sm: "auto" }}
          gap={{ xs: 0, sm: "16px" }}
        >
          <DateRange
            toDate={to}
            fromDate={from}
            setFromDate={setFrom}
            setToDate={setTo}
          />
          <SortBy selected={sortBy} options={options} setSelected={setSortBy} />
        </Box>
      </Box>

      <Card>
        <CardContent sx={{ p: { xs: "20px", sm: "47.5px 44.34px" } }}>
          {/* Outlet name & action buttons */}
          <Box
            display={"flex"}
            justifyContent={"space-between"}
            alignItems={["flexStart", "center"]}
            flexDirection={"row"}
          >
            <Typography sx={nameStyle}>{resturantName}</Typography>
            <Box display={"flex"} gap={"30px"}>
              {isSeller && (
                <Button
                  startIcon={<AddIcon height={"14px"} />}
                  variant="outlined"
                  sx={addNewButtonStyle}
                  onClick={() => {
                    setSelectedEntry(null);
                    setOpen(true);
                  }}
                >
                  ADD NEW
                </Button>
              )}
              <IconButton sx={{ ":hover": { backgroundColor: "transparent" } }}>
                <img
                  src={downloadIcon}
                  alt="download"
                  onClick={handleDownload}
                />
              </IconButton>
            </Box>
          </Box>

          {/* Records */}
          <Box sx={mainContentStyle} mt={"34px"}>
            <DataTable
              columns={columns.map((col) => ({
                ...col,
                labelStyle: labelStyle(isMobile),
                cellStyle: cellStyle(col.field, isMobile),
              }))}
              data={tableData}
              emptyStateMessage="No records available"
            />
          </Box>

          {/* Grand Total */}
          <Box>{!isMobile && grandTotalBlock}</Box>
          {!isMobile && <Divider />}

          {/* Pagination */}
          {!isMobile && (
            <Box sx={paginationContainerStyle}>
              <Typography variant="subtitle2" sx={paginationCaptionStyle}>
                Showing data {startIndex + 1} to {endIndex} of {totalRows}{" "}
                entries
              </Typography>
              <Pagination
                count={totalPages}
                page={+page}
                onChange={(_, newPage) => handleChangePage(newPage)}
                shape="rounded"
                variant="outlined"
                sx={paginationMobVer(isMobile)}
              />
            </Box>
          )}
        </CardContent>
      </Card>
      {isMobile && <Box sx={grandTotalMobVer}>{grandTotalBlock}</Box>}
      {/* Add/Edit Modal */}
      <AddNewLedger
        open={open}
        onClose={() => setOpen(false)}
        entry={selectedEntry}
        fetchLedgerData={fetchLedgerData}
        customerId={customerId ?? ""}
      />
    </>
  );
};

export default Ledger;
